import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, NavLink, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar, Button } from './ui';
import { NotificationsBell } from './NotificationsBell';
import { useAuth } from '../auth/AuthProvider';
import { cn } from '../lib/format';

/*
 * Only destinations that exist. A subscriptions feed would need a route no
 * agent has claimed this term (discovery owns /feed and /search, and neither is
 * subscriber-scoped), and a nav item that 404s is worse than one that is absent.
 */
const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/history', label: 'History', end: false },
  { to: '/studio', label: 'Your videos', end: false },
];

const SearchBox = (): JSX.Element => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [term, setTerm] = useState(params.get('q') ?? '');

  // Keeps the box in step with the URL when the viewer navigates back.
  useEffect(() => {
    setTerm(params.get('q') ?? '');
  }, [params]);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const trimmed = term.trim();
    if (trimmed === '') return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
  };

  return (
    <form onSubmit={submit} className="flex flex-1 justify-center px-4" role="search">
      <div className="flex w-full max-w-xl">
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search"
          aria-label="Search videos"
          className="w-full rounded-l-full border border-ink-600 bg-ink-800 px-4 py-2 text-sm text-ink-100 outline-none placeholder:text-ink-400 focus:border-brand-500"
        />
        <button
          type="submit"
          className="rounded-r-full border border-l-0 border-ink-600 bg-ink-700 px-5 text-sm text-ink-200 hover:bg-ink-600"
        >
          Search
        </button>
      </div>
    </form>
  );
};

const AccountMenu = (): JSX.Element => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      if (container.current !== null && !container.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (user === null) {
    return (
      <Link to="/login">
        <Button variant="secondary">Sign in</Button>
      </Link>
    );
  }

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <Avatar src={user.channel.avatarUrl} name={user.channel.displayName} size={34} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl border border-ink-600 bg-ink-800 py-1 shadow-xl"
        >
          <div className="border-b border-ink-700 px-4 py-3">
            <p className="truncate text-sm font-medium text-ink-100">{user.channel.displayName}</p>
            <p className="truncate text-xs text-ink-400">@{user.channel.handle}</p>
          </div>
          <Link
            to={`/@${user.channel.handle}`}
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-ink-200 hover:bg-ink-700"
          >
            Your channel
          </Link>
          <Link
            to="/studio"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-ink-200 hover:bg-ink-700"
          >
            Your videos
          </Link>
          <Link
            to="/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-ink-200 hover:bg-ink-700"
          >
            Channel settings
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
            className="block w-full px-4 py-2 text-left text-sm text-ink-200 hover:bg-ink-700"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
};

export const AppShell = (): JSX.Element => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-ink-900 text-ink-100">
      <header className="sticky top-0 z-30 flex items-center gap-2 border-b border-ink-700 bg-ink-900/95 px-4 py-2 backdrop-blur">
        <Link to="/" className="flex items-center gap-2 px-2 text-lg font-semibold tracking-tight">
          <span className="inline-block h-5 w-5 rounded bg-brand-500" aria-hidden="true" />
          Streamr
        </Link>
        <SearchBox />
        <div className="flex items-center gap-2">
          <Link to="/upload">
            <Button variant="secondary">Upload</Button>
          </Link>
          {user !== null && <NotificationsBell />}
          <AccountMenu />
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1600px]">
        <nav className="hidden w-56 shrink-0 flex-col gap-1 p-4 lg:flex" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'rounded-lg px-3 py-2 text-sm',
                  isActive ? 'bg-ink-700 text-ink-100' : 'text-ink-300 hover:bg-ink-800',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <main className="min-w-0 flex-1 px-4 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
